import { Box, Stack } from '@mui/material';
import {
    ProgressZonedropZone,
    ProgressZoneprogressDesc,
    ProgressZoneprogressStatus,
    iconsPicker,
    ProgressZoneprogressColor,
    ProgressZonebaseColor,
    ProgressZoneprogressText,
    ProgressZonebackgroundProgress,
    ProgressZoneBoxParent,
    ProgressZoneBoxStepParent,
    ProgressZoneBoxDesc,
    ProgressZoneIconsPicker,
    ProgressZoneProgressStatus,
    ProgressZoneFailedUpload,
    ProgressZonePresumeUpload,
} from 'assets/styles/modules/upload-documents/upload-packet/ProgressZone';

import { ReactElement, useEffect, useLayoutEffect, useState } from 'react';
import { ButtonComp } from 'shared/components/AppComponents';
import TypographyLevel from 'shared/components/Typography';
import { Toast } from 'shared/helpers';

const ProgressBox = ({ data, index }: ProgressBox): ReactElement => {
    const [temporaryStatus, setTemporaryStatus] = useState<string>('wait');

    useEffect(() => {
        if (data.progress === 100) {
            setTemporaryStatus('success');
        }
        if (data.progress > 0 && data.progress < 100) {
            setTemporaryStatus('loading');
        }
        if (data.progress < 0) {
            setTemporaryStatus('failed');
        }
        if (data.progress === 0) {
            setTemporaryStatus('wait');
        }
    }, [data]);

    return (
        <Box
            sx={{
                borderRadius: '10px',
                background: ProgressZonebackgroundProgress(temporaryStatus),
            }}
        >
            <Box sx={ProgressZoneBoxParent(index)}>
                <Box sx={ProgressZoneprogressText}>
                    <Box sx={ProgressZoneBoxStepParent}>
                        <TypographyLevel
                            variant="four"
                            sx={ProgressZoneprogressDesc(temporaryStatus)}
                        >
                            {data.name}
                        </TypographyLevel>
                    </Box>
                </Box>
                <Box sx={ProgressZoneprogressText}>
                    <Box sx={ProgressZoneBoxDesc}>
                        <TypographyLevel
                            variant="four"
                            sx={ProgressZoneprogressStatus(temporaryStatus)}
                        >
                            {temporaryStatus === 'wait' && 'Ready to upload'}
                            {temporaryStatus === 'success' && 'Uploaded'}
                            {temporaryStatus === 'failed' && 'Upload Failed!'}
                            {temporaryStatus === 'loading' && 'Uploading...'}

                            {temporaryStatus === 'success' && (
                                <img
                                    src={`/images/icons/${iconsPicker(
                                        temporaryStatus
                                    )}`}
                                    style={ProgressZoneIconsPicker}
                                    alt="successful uploading"
                                />
                            )}
                        </TypographyLevel>
                    </Box>
                </Box>
            </Box>

            {temporaryStatus === 'loading' && (
                <Box sx={ProgressZoneProgressStatus}>
                    <Box
                        sx={ProgressZoneprogressColor}
                        width={`${data.progress}%`}
                    />
                    <Box sx={ProgressZonebaseColor} />
                </Box>
            )}
        </Box>
    );
};

/**
 * Renders the ProgressZone component.
 *
 * @param {UploadFile} param - The component props.
 * @returns {ReactElement} - The rendered component.
 */
const ProgressZone = ({
    fileCompleted,
    fileReUpload,
    fileReinit,
    state,
    setState,
    isReinitialize,
    isRework,
    isFetchError,
}: UploadFile): ReactElement => {
    const [isRestatus, setRestatus] = useState<boolean>(false);
    const [isReinstate, setReinstate] = useState<boolean>(false);
    /**
     * Handles the completion of file processing.
     *
     * @returns {void}
     */
    const handleFileCompleted = (): void => {
        console.log('file completed');
        fileCompleted(state.fileBlob);
    };

    /**
     * Monitors the progress of file uploads and calls handleFileCompleted if all files are completed.
     *
     * @param {Array} fileBlob - The array of file data objects to monitor.
     */
    useEffect(() => {
        /**
         * Checks if all file uploads are completed and none are -1.
         *
         * @type {boolean}
         */
        const allCompleted = state.fileBlob.every(
            /**
             * Checks if the progress of a file is not zero and is either 100 or -1.
             *
             * @param {Object} data - The file data object.
             * @returns {boolean} - True if the progress is not zero and is either 100 or -1, false otherwise.
             */
            (data: any) =>
                data.progress !== 0 &&
                (data.progress === 100 || data.progress === -1)
        );

        // Check if not all data progress are -1, then call handleFileCompleted
        if (
            allCompleted &&
            state.fileBlob.some((data: any) => data.progress !== -1) &&
            state.fileStatus === 'Created'
        ) {
            handleFileCompleted();
        }
        if (state.fileStatus !== '-' && state.fileStatus !== 'Created') {
            Toast.showErrorMessage('Failed upload packet, Please try again!');
            setState({ ...state, uploadProgress: false });
        }
        if (state.fileBlob.every((data: any) => data.progress === -1)) {
            setState({ ...state, failedUpload: true });
        }
        if (
            state.fileBlob.some((data: any, i: number) =>
                state.fileFailure.includes(i)
            )
        ) {
            setState({ ...state, failedBlob: true });
        }
    }, [
        state.fileBlob,
        state.fileStatus,
        state.fileFailure,
        isRestatus,
        state.failedBlob,
    ]);

    const [fileReinitialize, setFileReinitialize] = useState<boolean>(false);

    useEffect(() => {
        if (fileReinitialize) {
            if (
                state.fileBlob.some(
                    (data: any, i: number) =>
                        state.fileFailure.includes(i) && data.progress !== 100
                ) === false
            ) {
                setState({ ...state, failedBlob: false, fileFailure: [] });
            }
            setFileReinitialize(false);
        }
    }, [fileReinitialize]);
    useLayoutEffect(() => {
        if (
            state.failedBlob === false &&
            isReinstate &&
            typeof fileReUpload === 'function'
        ) {
            fileReUpload();
            setReinstate(false);
        } else if (
            state.failedBlob === true &&
            isRestatus &&
            typeof fileReinit === 'function'
        ) {
            console.log('reinit');
            fileReinit(() => {
                setFileReinitialize(true);
            });
            setRestatus(false);
        }
    }, [isReinstate, isRestatus]);

    return (
        <>
            <Box sx={ProgressZonedropZone}>
                <Box>
                    {state.fileBlob &&
                        state.fileBlob.length > 0 &&
                        state.fileBlob.map((data: any, index: number) => (
                            <ProgressBox data={data} index={index} />
                        ))}
                </Box>
            </Box>

            <Stack
                direction="row"
                spacing={2}
                width="32rem"
                justifyContent="center"
                alignSelf="center"
            >
                {/* Button for failed upload only */}
                {(state.failedUpload || state.failedBlob) && (
                    <ButtonComp
                        sx={ProgressZoneFailedUpload}
                        width="16rem"
                        onClick={() =>
                            setState({
                                ...state,
                                uploadProgress: false,
                                failedBlob: false,
                                fileBlob: Array.from(state.fileBlob).map(
                                    (e: any) => ({
                                        ...e,
                                        progress: 0,
                                    })
                                ),
                            })
                        }
                        type="neutral"
                    >
                        Go back & Edit
                    </ButtonComp>
                )}
                {(isReinitialize || state.failedBlob) &&
                    (typeof isRework === 'undefined' ||
                        (typeof isRework === 'boolean' &&
                            isRework === false)) && (
                        <ButtonComp
                            sx={ProgressZonePresumeUpload}
                            width="16rem"
                            onClick={() => {
                                setState({
                                    ...state,
                                    fileBlob: Array.from(state.fileBlob).map(
                                        (e: any, i: number) => ({
                                            ...e,
                                            progress:
                                                isReinitialize ||
                                                isFetchError ||
                                                (state.failedBlob === true &&
                                                    state.fileFailure.includes(
                                                        i
                                                    ))
                                                    ? 0
                                                    : e.progress,
                                        })
                                    ),
                                    fileNetworkFailure: [],
                                });
                                if (isReinitialize || isFetchError) {
                                    setReinstate(true);
                                } else if (state.failedBlob) {
                                    setRestatus(true);
                                }
                            }}
                            type="secondary"
                        >
                            Presume Uploading
                        </ButtonComp>
                    )}
            </Stack>
        </>
    );
};

export default ProgressZone;
